import { Controller } from "@hotwired/stimulus";

const DEFAULT_MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 180;

export default class extends Controller {
  static values = {
    url: String,
    minQueryLength: { type: Number, default: DEFAULT_MIN_QUERY_LENGTH },
    autoSubmit: Boolean,
    jumpToMatch: Boolean,
    multiple: Boolean,
    deferValueChanges: Boolean
  };

  static targets = [ "value", "search", "suggestions", "list", "status", "selectedList", "clearButton" ];

  connect() {
    this.searchDebounceTimer = null;
    this.currentRequest = null;
    this.suggestionButtons = [];
    this.activeSuggestionIndex = -1;
    this.dismissedQuery = null;
    this.boundWindowClick = this.handleWindowClick.bind(this);
    window.addEventListener("click", this.boundWindowClick);
  }

  disconnect() {
    this.abortRequest();
    this.clearSearchDebounce();
    window.removeEventListener("click", this.boundWindowClick);
  }

  handleInput(event) {
    const query = this.normalized(event.target.value);
    const selectedLabel = this.element.dataset.recordAutocompleteSelectedLabel || "";
    this.dismissedQuery = null;
    if (!this.multipleValue && this.hasValueTarget && this.valueTarget.value && query !== this.normalized(selectedLabel) && !this.deferValueChangesValue) {
      this.clearValue({ dispatch: true });
    }

    this.clearSearchDebounce();
    this.abortRequest();

    if (this.deferValueChangesValue && !query) {
      this.dispatchDeferredValueSelection("", "");
    }

    if (query.length < this.minimumQueryLength()) {
      this.hideSuggestions();
      return;
    }

    this.searchDebounceTimer = window.setTimeout(() => {
      this.searchDebounceTimer = null;
      this.fetchSuggestions(query);
    }, SEARCH_DEBOUNCE_MS);
  }

  handleFocus() {
    const query = this.normalized(this.searchTarget.value);
    if (!this.multipleValue && this.hasValueTarget && this.valueTarget.value) return;
    if (query.length < this.minimumQueryLength()) return;

    this.dismissedQuery = null;
    this.clearSearchDebounce();
    this.fetchSuggestions(query);
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      const suggestionsOpen = this.hasSuggestionsTarget && !this.suggestionsTarget.classList.contains("hidden");
      const autocompletePending = Boolean(this.searchDebounceTimer || this.currentRequest);
      if (!suggestionsOpen && !autocompletePending) return;

      event.preventDefault();
      event.stopPropagation();
      this.dismissSuggestions(this.normalized(this.searchTarget.value));
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (this.suggestionButtons.length === 0) {
        const query = this.normalized(this.searchTarget.value);
        if (query.length >= this.minimumQueryLength()) {
          this.dismissedQuery = null;
          this.fetchSuggestions(query);
        }
        return;
      }

      this.setActiveSuggestionIndex(this.activeSuggestionIndex + (event.key === "ArrowDown" ? 1 : -1));
      return;
    }

    if (event.key !== "Enter") return;
    if (this.activeSuggestionIndex < 0 || !this.suggestionButtons[this.activeSuggestionIndex]) return;

    event.preventDefault();
    this.suggestionButtons[this.activeSuggestionIndex].click();
  }

  chooseSuggestion(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const id = button.dataset.recordId;
    const label = this.normalized(button.dataset.recordLabel);
    if (!id || !label) return;

    if (this.multipleValue) {
      this.addMultipleSelection(id, label);
      return;
    }

    this.searchTarget.value = label;
    if (this.deferValueChangesValue) {
      this.dispatchDeferredValueSelection(id, label);
    } else {
      this.valueTarget.value = id;
      this.element.dataset.recordAutocompleteSelectedLabel = label;
      this.dispatchValueChange();
    }
    this.dismissSuggestions(label);
    this.showClearButton();

    if (this.autoSubmitValue) this.submitForm();
  }

  clearSelection(event) {
    event.preventDefault();
    if (this.multipleValue) return;

    if (this.deferValueChangesValue) {
      this.hideClearButton();
      this.dispatchDeferredValueSelection("", "");
    } else {
      this.clearValue({ dispatch: true });
    }
    this.searchTarget.value = "";
    this.dismissSuggestions("");
    this.searchTarget.focus();
    if (this.autoSubmitValue) this.submitForm();
  }

  removeSelection(event) {
    event.preventDefault();
    if (!this.multipleValue) return;

    const removeButtons = this.hasSelectedListTarget
      ? Array.from(this.selectedListTarget.querySelectorAll(".ec-record-autocomplete-remove"))
      : [];
    const removedIndex = removeButtons.indexOf(event.currentTarget);
    const id = event.currentTarget.dataset.recordId;
    this.valueElements()
      .filter(valueTarget => valueTarget.value === id)
      .forEach(valueTarget => valueTarget.remove());
    this.renderSelectedList();
    this.dispatchValueChange();

    const remainingButtons = this.hasSelectedListTarget
      ? Array.from(this.selectedListTarget.querySelectorAll(".ec-record-autocomplete-remove"))
      : [];
    const focusIndex = Math.min(Math.max(removedIndex, 0), remainingButtons.length - 1);
    (remainingButtons[focusIndex] || this.searchTarget).focus();
  }

  fetchSuggestions(query) {
    if (!query || query.length < this.minimumQueryLength() || !this.hasUrlValue) return;

    this.abortRequest();
    const request = new AbortController();
    this.currentRequest = request;
    const endpoint = new URL(this.urlValue, window.location.origin);
    endpoint.searchParams.set("q", query);

    fetch(endpoint.toString(), {
      headers: { Accept: "application/json" },
      signal: request.signal
    })
      .then(response => (response.ok ? response.json() : []))
      .then(results => {
        if (this.currentRequest !== request) return;
        if (!this.hasSearchTarget) return;
        if (this.normalized(this.searchTarget.value) !== query) return;
        if (this.dismissedQuery === query) return;
        this.renderSuggestions(Array.isArray(results) ? results : []);
      })
      .catch(error => {
        if (error?.name === "AbortError") return;
        if (this.currentRequest !== request) return;
        if (!this.hasSearchTarget) return;
        if (this.normalized(this.searchTarget.value) !== query) return;
        if (this.dismissedQuery === query) return;
        this.renderSuggestions([]);
      })
      .finally(() => {
        if (this.currentRequest === request) this.currentRequest = null;
      });
  }

  renderSuggestions(results) {
    if (!this.hasSuggestionsTarget || !this.hasListTarget || !this.hasStatusTarget) return;

    this.listTarget.innerHTML = "";
    this.suggestionButtons = [];
    this.activeSuggestionIndex = -1;
    const normalizedResults = results
      .filter(result => result?.id && (result.label || result.name))
      .slice(0, 8)
      .map(result => ({ id: result.id.toString(), label: this.normalized(result.label || result.name) }));

    if (normalizedResults.length === 0) {
      this.statusTarget.textContent = "No matches found.";
      this.showSuggestions();
      return;
    }

    this.statusTarget.textContent = "Choose a match";
    normalizedResults.forEach((result, index) => {
      const item = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "ec-dish-combobox-option ec-dish-combobox-option--existing";
      button.id = `${this.searchTarget.id}_option_${index}`;
      button.dataset.action = "click->record-autocomplete#chooseSuggestion";
      button.dataset.recordId = result.id;
      button.dataset.recordLabel = result.label;
      button.setAttribute("role", "option");
      button.setAttribute("aria-selected", "false");
      button.textContent = result.label;
      item.appendChild(button);
      this.listTarget.appendChild(item);
      this.suggestionButtons.push(button);
    });

    this.showSuggestions();
    this.setActiveSuggestionIndex(0);
  }

  addMultipleSelection(id, label) {
    if (this.valueElements().some(valueTarget => valueTarget.value === id)) {
      this.searchTarget.value = "";
      this.dismissSuggestions("");
      return;
    }

    const valueTarget = document.createElement("input");
    valueTarget.type = "hidden";
    valueTarget.name = this.element.dataset.recordAutocompleteFieldName;
    valueTarget.value = id;
    valueTarget.dataset.recordLabel = label;
    valueTarget.dataset.recordAutocompleteTarget = "value";
    this.element.insertBefore(valueTarget, this.searchTarget);
    this.renderSelectedList();
    this.searchTarget.value = "";
    this.dispatchValueChange();
    this.dismissSuggestions("");
  }

  renderSelectedList() {
    if (!this.multipleValue || !this.hasSelectedListTarget) return;

    this.selectedListTarget.innerHTML = "";
    this.valueElements().forEach(valueTarget => {
      const label = valueTarget.dataset.recordLabel;
      if (!label) return;

      const item = document.createElement("span");
      item.className = "ec-record-autocomplete-selected-item";
      const labelElement = document.createElement("span");
      labelElement.className = "ec-record-autocomplete-selected-label";
      labelElement.textContent = label;
      item.appendChild(labelElement);
      const removeButton = document.createElement("button");
      removeButton.type = "button";
      removeButton.className = "ec-record-autocomplete-remove";
      removeButton.setAttribute("aria-label", `Remove ${label}`);
      removeButton.dataset.action = "click->record-autocomplete#removeSelection";
      removeButton.dataset.recordId = valueTarget.value;
      removeButton.textContent = "×";
      item.appendChild(removeButton);
      this.selectedListTarget.appendChild(item);
    });
  }

  showSuggestions() {
    this.suggestionsTarget.classList.remove("hidden");
    this.comboboxShell().classList.add("ec-dish-combobox-shell--open");
    this.searchTarget.setAttribute("aria-expanded", "true");
  }

  hideSuggestions() {
    this.comboboxShell().classList.remove("ec-dish-combobox-shell--open");
    if (!this.hasSuggestionsTarget) return;

    this.suggestionsTarget.classList.add("hidden");
    this.searchTarget.setAttribute("aria-expanded", "false");
    this.searchTarget.removeAttribute("aria-activedescendant");
    this.listTarget.innerHTML = "";
    this.statusTarget.textContent = "";
    this.suggestionButtons = [];
    this.activeSuggestionIndex = -1;
  }

  setActiveSuggestionIndex(nextIndex) {
    if (this.suggestionButtons.length === 0) return;

    const normalizedIndex = ((nextIndex % this.suggestionButtons.length) + this.suggestionButtons.length) % this.suggestionButtons.length;
    this.activeSuggestionIndex = normalizedIndex;
    this.suggestionButtons.forEach((button, index) => {
      const active = index === normalizedIndex;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-selected", active ? "true" : "false");
      if (active) button.scrollIntoView({ block: "nearest" });
    });
    this.searchTarget.setAttribute("aria-activedescendant", this.suggestionButtons[normalizedIndex].id);
  }

  clearValue({ dispatch = false } = {}) {
    if (this.hasValueTarget) this.valueTarget.value = "";
    this.element.dataset.recordAutocompleteSelectedLabel = "";
    this.hideClearButton();
    if (dispatch) this.dispatchValueChange();
  }

  showClearButton() {
    if (this.hasClearButtonTarget) this.clearButtonTarget.classList.remove("hidden");
  }

  hideClearButton() {
    if (this.hasClearButtonTarget) this.clearButtonTarget.classList.add("hidden");
  }

  submitForm() {
    const form = this.element.closest("form");
    if (!form) return;

    if (this.jumpToMatchValue) {
      const jumpField = form.querySelector("#meal-log-jump-to-match");
      if (jumpField) jumpField.value = "1";
    }

    form.requestSubmit();
  }

  dispatchValueChange() {
    if (!this.hasValueTarget) return;

    this.valueTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  dispatchDeferredValueSelection(id, label) {
    if (!this.hasValueTarget) return;

    this.valueTarget.dispatchEvent(new CustomEvent("record-autocomplete:selection", {
      bubbles: true,
      detail: { id, label }
    }));
  }

  valueElements() {
    return Array.from(this.element.querySelectorAll("[data-record-autocomplete-target~='value']"));
  }

  handleWindowClick(event) {
    if (!this.element.contains(event.target)) this.dismissSuggestions();
  }

  minimumQueryLength() {
    return this.hasMinQueryLengthValue ? this.minQueryLengthValue : DEFAULT_MIN_QUERY_LENGTH;
  }

  normalized(value) {
    return (value || "").trim().replace(/\s+/g, " ");
  }

  abortRequest() {
    if (!this.currentRequest) return;

    this.currentRequest.abort();
    this.currentRequest = null;
  }

  dismissSuggestions(query = null) {
    const dismissedQuery = query ?? (this.hasSearchTarget ? this.normalized(this.searchTarget.value) : "");

    this.dismissedQuery = dismissedQuery;
    this.abortRequest();
    this.clearSearchDebounce();
    this.hideSuggestions();
  }

  comboboxShell() {
    return this.element.closest(".ec-dish-combobox-shell") || this.element;
  }

  clearSearchDebounce() {
    if (!this.searchDebounceTimer) return;

    window.clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = null;
  }
}
